import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import type { Categoria } from '../api';
import { formattaEuro } from '../formato';

// ── Avvisi ──────────────────────────────────────────────────────────────────

type Avviso = { id: number; testo: string; tono: 'ok' | 'errore' | 'attenzione' };
const ContestoAvvisi = createContext<(testo: string, tono?: Avviso['tono']) => void>(() => {});

export const useAvvisi = () => useContext(ContestoAvvisi);

export function FornitoreAvvisi({ children }: { children: ReactNode }) {
  const [avvisi, setAvvisi] = useState<Avviso[]>([]);
  const prossimo = useRef(1);

  const avvisa = useCallback((testo: string, tono: Avviso['tono'] = 'ok') => {
    const id = prossimo.current++;
    setAvvisi((a) => [...a, { id, testo, tono }]);
    // Gli errori restano più a lungo: sono da leggere, non da intravedere.
    setTimeout(() => setAvvisi((a) => a.filter((x) => x.id !== id)), tono === 'errore' ? 9000 : 4500);
  }, []);

  return (
    <ContestoAvvisi.Provider value={avvisa}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2">
        {avvisi.map((a) => (
          <div
            key={a.id}
            role="status"
            className={`pointer-events-auto rounded-md border px-4 py-3 text-sm shadow-lg ${
              a.tono === 'errore'
                ? 'border-allerta/30 bg-allerta-chiaro text-allerta'
                : a.tono === 'attenzione'
                  ? 'border-attenzione/30 bg-attenzione-chiaro text-attenzione'
                  : 'border-incassi/30 bg-incassi-chiaro text-incassi'
            }`}
          >
            {a.testo}
            <button
              onClick={() => setAvvisi((v) => v.filter((x) => x.id !== a.id))}
              className="float-right -mt-0.5 ml-3 text-lg leading-none opacity-60 hover:opacity-100"
              aria-label="Chiudi"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ContestoAvvisi.Provider>
  );
}

// ── Caricamento dati ────────────────────────────────────────────────────────

export interface StatoCarico<T> {
  dati: T | null;
  caricamento: boolean;
  errore: string | null;
  ricarica: () => void;
}

/** Carica dall'API tenendo separati i tre stati: dato, attesa, errore. */
export function useCarico<T>(carica: () => Promise<T>, dipendenze: unknown[] = []): StatoCarico<T> {
  const [dati, setDati] = useState<T | null>(null);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [giro, setGiro] = useState(0);

  useEffect(() => {
    let vivo = true;
    setCaricamento(true);
    setErrore(null);
    carica()
      .then((r) => vivo && setDati(r))
      .catch((e) => vivo && setErrore(e instanceof Error ? e.message : 'Errore di caricamento'))
      .finally(() => vivo && setCaricamento(false));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dipendenze, giro]);

  return { dati, caricamento, errore, ricarica: () => setGiro((g) => g + 1) };
}

export function Caricamento({ testo = 'Caricamento…' }: { testo?: string }) {
  return <div className="p-8 text-center text-sm text-inchiostro-500">{testo}</div>;
}

export function Errore({ testo, riprova }: { testo: string; riprova?: () => void }) {
  return (
    <div className="rounded-md border border-allerta/30 bg-allerta-chiaro p-4 text-sm text-allerta">
      <p className="font-medium">{testo}</p>
      {riprova && (
        <button onClick={riprova} className="mt-2 underline underline-offset-2">
          Riprova
        </button>
      )}
    </div>
  );
}

/** Cosa mostrare quando non c'è niente: mai una tabella vuota senza spiegazione. */
/**
 * Navigazione a pagine per gli elenchi lunghi. Mostra sempre **da dove a dove**
 * e su quanti: un elenco che non dichiara di essere una fetta fa credere che i
 * dati mancanti non ci siano, ed è il modo più veloce per far perdere fiducia
 * nei numeri.
 */
export function Paginazione({
  scarto,
  mostrati,
  totale,
  limite,
  onPagina,
}: {
  scarto: number;
  mostrati: number;
  totale: number;
  limite: number;
  onPagina: (scarto: number) => void;
}) {
  if (totale <= mostrati && scarto === 0) return null;
  const primo = totale === 0 ? 0 : scarto + 1;
  const ultimo = scarto + mostrati;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-inchiostro-200 p-3 text-sm">
      <span className="text-inchiostro-500">
        Da <span className="num">{primo}</span> a <span className="num">{ultimo}</span> di{' '}
        <span className="num font-medium">{totale}</span>
      </span>
      <div className="flex gap-2">
        <button
          className="bottone-secondario"
          disabled={scarto === 0}
          onClick={() => onPagina(Math.max(0, scarto - limite))}
        >
          Precedenti
        </button>
        <button className="bottone-secondario" disabled={ultimo >= totale} onClick={() => onPagina(scarto + limite)}>
          Successivi
        </button>
      </div>
    </div>
  );
}

export function Vuoto({ titolo, testo, azione }: { titolo: string; testo?: string; azione?: ReactNode }) {
  return (
    <div className="p-10 text-center">
      <p className="text-sm font-medium text-inchiostro-700">{titolo}</p>
      {testo && <p className="mx-auto mt-1 max-w-md text-sm text-inchiostro-500">{testo}</p>}
      {azione && <div className="mt-4">{azione}</div>}
    </div>
  );
}

// ── Elementi di presentazione ───────────────────────────────────────────────

export function Importo({
  cent,
  tono,
  grassetto,
  segno,
}: {
  cent: number;
  tono?: 'saldo' | 'incassi' | 'uscite' | 'neutro' | 'auto';
  grassetto?: boolean;
  segno?: boolean;
}) {
  const t =
    tono === 'auto' ? (cent < 0 ? 'uscite' : cent > 0 ? 'incassi' : 'neutro') : (tono ?? 'neutro');
  const colore =
    t === 'saldo'
      ? cent < 0
        ? 'text-allerta'
        : 'text-saldo'
      : t === 'incassi'
        ? 'text-incassi'
        : t === 'uscite'
          ? 'text-uscite'
          : 'text-inchiostro-800';
  return (
    <span className={`num ${colore} ${grassetto ? 'font-semibold' : ''}`}>
      {segno && cent > 0 ? '+ ' : ''}
      {formattaEuro(cent)}
    </span>
  );
}

export function Etichetta({
  testo,
  tono = 'neutro',
  titolo,
}: {
  testo: string;
  tono?: 'neutro' | 'ok' | 'attenzione' | 'allerta' | 'info';
  titolo?: string;
}) {
  const classi = {
    neutro: 'bg-inchiostro-100 text-inchiostro-700 border-inchiostro-200',
    ok: 'bg-incassi-chiaro text-incassi border-incassi/20',
    attenzione: 'bg-attenzione-chiaro text-attenzione border-attenzione/20',
    allerta: 'bg-allerta-chiaro text-allerta border-allerta/20',
    info: 'bg-saldo-chiaro text-saldo border-saldo/20',
  }[tono];
  return (
    <span title={titolo} className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-xs font-medium ${classi}`}>
      {testo}
    </span>
  );
}

export function Scheda({
  titolo,
  sottotitolo,
  azioni,
  children,
  senzaPadding,
}: {
  titolo?: string;
  sottotitolo?: ReactNode;
  azioni?: ReactNode;
  children: ReactNode;
  senzaPadding?: boolean;
}) {
  return (
    <section className="scheda overflow-hidden">
      {(titolo || azioni) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-inchiostro-200 px-4 py-3">
          <div>
            {titolo && <h2 className="text-sm font-semibold text-inchiostro-900">{titolo}</h2>}
            {sottotitolo && <p className="mt-0.5 text-xs text-inchiostro-500">{sottotitolo}</p>}
          </div>
          {azioni && <div className="flex flex-wrap items-center gap-2">{azioni}</div>}
        </header>
      )}
      <div className={senzaPadding ? '' : 'p-4'}>{children}</div>
    </section>
  );
}

export function Dialogo({
  aperto,
  titolo,
  onChiudi,
  children,
  larghezza = 'max-w-2xl',
}: {
  aperto: boolean;
  titolo: string;
  onChiudi: () => void;
  children: ReactNode;
  larghezza?: string;
}) {
  useEffect(() => {
    if (!aperto) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [aperto, onChiudi]);

  if (!aperto) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-inchiostro-950/40 p-4 pt-16">
      <div className={`w-full ${larghezza} rounded-lg bg-white shadow-xl`} role="dialog" aria-modal="true">
        <header className="flex items-center justify-between border-b border-inchiostro-200 px-5 py-3">
          <h2 className="text-sm font-semibold">{titolo}</h2>
          <button onClick={onChiudi} className="text-xl leading-none text-inchiostro-400 hover:text-inchiostro-800" aria-label="Chiudi">
            ×
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export function Campo({
  etichetta,
  aiuto,
  children,
  larghezza = '',
}: {
  etichetta: string;
  aiuto?: string;
  children: ReactNode;
  larghezza?: string;
}) {
  return (
    <label className={`block ${larghezza}`}>
      <span className="etichetta">{etichetta}</span>
      {children}
      {aiuto && <span className="mt-1 block text-xs text-inchiostro-500">{aiuto}</span>}
    </label>
  );
}

/**
 * Campo importo: si scrive in euro con la virgola, si tiene in centesimi.
 * La conversione passa dalle cifre, non da `parseFloat`.
 *
 * Il testo digitato vive qui dentro finché il campo è a fuoco, e solo
 * all'uscita torna alla forma normalizzata. Non è un dettaglio: un campo che
 * riformatta a ogni tasto rende **impossibile scrivere i decimali** — si batte
 * `4`, compare `4,00`, il cursore salta in fondo, e da lì in poi il numero fa
 * quello che vuole. Chi scrive un importo lo scrive da sinistra a destra, e il
 * campo deve lasciarglielo finire.
 */
export function CampoImporto({
  valoreCent,
  onCambia,
  className = 'campo',
  disabilitato,
  ariaEtichetta,
}: {
  valoreCent: number;
  onCambia: (cent: number) => void;
  className?: string;
  disabilitato?: boolean;
  /** Per i campi dentro una tabella, dove l'intestazione di colonna non basta. */
  ariaEtichetta?: string;
}) {
  /**
   * Zero si mostra **vuoto**, non «0,00».
   *
   * Uno zero scritto è un ostacolo: si clicca nel campo, si comincia a battere,
   * e le cifre finiscono intorno a quello che c'era già — «4000,50» diventa
   * «4000,500,00». Un campo ancora da compilare deve essere vuoto, e il
   * segnaposto dice cosa ci va.
   */
  const formatta = (cent: number) => (cent === 0 ? '' : (cent / 100).toFixed(2).replace('.', ','));
  const [testo, setTesto] = useState(() => formatta(valoreCent));
  const [modificando, setModificando] = useState(false);

  useEffect(() => {
    if (!modificando) setTesto(formatta(valoreCent));
  }, [valoreCent, modificando]);

  return (
    <input
      className={`${className} text-right num`}
      inputMode="decimal"
      aria-label={ariaEtichetta}
      placeholder="0,00"
      value={testo}
      disabled={disabilitato}
      onFocus={(e) => {
        setModificando(true);
        // Entrando si seleziona quello che c'è: correggere un importo vuol dire
        // riscriverlo, non infilare cifre in mezzo a quelle vecchie.
        e.target.select();
      }}
      onChange={(e) => {
        setTesto(e.target.value);
        const pulito = e.target.value.replace(/\./g, '').replace(',', '.');
        const n = Number(pulito);
        if (pulito !== '' && Number.isFinite(n)) onCambia(Math.round(n * 100));
        if (pulito === '') onCambia(0);
      }}
      onBlur={() => {
        setModificando(false);
        setTesto(formatta(valoreCent));
      }}
    />
  );
}

/**
 * Collegamento a una fattura che **si ricorda da dove si è partiti**, filtri
 * compresi.
 *
 * Il tasto «indietro» del browser lo farebbe già da sé, ora che i filtri stanno
 * nell'indirizzo. Questo serve al collegamento «← Fatture attive» in cima al
 * dettaglio, che altrimenti riporta all'elenco intero: chi lavora quattro
 * fatture dello stesso cliente rifarebbe la ricerca quattro volte.
 */
export function LinkFattura({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: ReactNode;
}) {
  const percorso = useLocation();
  return (
    <Link to={`/fatture/${id}`} state={{ elenco: `${percorso.pathname}${percorso.search}` }} className={className}>
      {children}
    </Link>
  );
}

/**
 * I filtri di un elenco tenuti **nell'indirizzo**, non nello stato del
 * componente.
 *
 * Il motivo è un gesto preciso: cerco un cliente fra le fatture, entro in una,
 * faccio quello che devo fare, torno indietro — e mi ritrovo l'elenco intero.
 * Con quattro fatture da compensare quel giro si rifà quattro volte. Lo stato
 * di React muore quando la pagina si smonta; l'indirizzo no, e il tasto
 * «indietro» del browser lo riporta esattamente com'era.
 *
 * In più diventano un indirizzo che si può tenere aperto in due schede o
 * mandare a un collega, cosa che prima non esisteva.
 *
 * Si scrive sempre in **sostituzione**: senza, ogni lettera digitata nella
 * ricerca lascerebbe una voce nella cronologia, e «indietro» tornerebbe a
 * «PLANE», «PLAN», «PLA»… invece che all'elenco da cui si era partiti. I campi
 * vuoti spariscono dall'indirizzo, che resta leggibile.
 */
export function useFiltriUrl<T extends Record<string, string>>(
  iniziali: T,
): { filtri: T; imposta: (patch: Partial<T>) => void; azzera: () => void; qualcuno: boolean } {
  const [parametri, setParametri] = useSearchParams();

  const filtri = useMemo(() => {
    const v = { ...iniziali };
    for (const chiave of Object.keys(iniziali) as (keyof T)[]) {
      const letto = parametri.get(String(chiave));
      if (letto !== null) v[chiave] = letto as T[keyof T];
    }
    return v;
    // `parametri` cambia identità a ogni navigazione: è la dipendenza vera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametri]);

  /**
   * Si parte da `window.location.search`, non dallo stato di React.
   *
   * Non è pignoleria: due filtri cambiati a distanza di pochi millisecondi —
   * si scrive nella ricerca e subito si apre la tendina — arrivano prima che
   * React abbia riemesso il componente, e il secondo si calcolerebbe su
   * parametri vecchi **cancellando il primo**. L'indirizzo invece è già
   * aggiornato: `history.replaceState` è sincrono, quindi è lui il valore
   * corrente, non la copia che il componente ha in mano.
   */
  const imposta = useCallback(
    (patch: Partial<T>) => {
      const p = new URLSearchParams(window.location.search);
      for (const [chiave, valore] of Object.entries(patch)) {
        if (valore === undefined || valore === '') p.delete(chiave);
        else p.set(chiave, String(valore));
      }
      setParametri(p, { replace: true });
    },
    [setParametri],
  );

  const azzera = useCallback(() => {
    const p = new URLSearchParams(window.location.search);
    for (const chiave of Object.keys(iniziali)) p.delete(chiave);
    setParametri(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setParametri]);

  const qualcuno = (Object.keys(iniziali) as (keyof T)[]).some((c) => filtri[c] !== iniziali[c]);

  return { filtri, imposta, azzera, qualcuno };
}

export function useDebounce<T>(valore: T, ritardo = 300): T {
  const [v, setV] = useState(valore);
  useEffect(() => {
    const t = setTimeout(() => setV(valore), ritardo);
    return () => clearTimeout(t);
  }, [valore, ritardo]);
  return v;
}

/** Albero delle categorie con il rientro calcolato, profondità libera. */
export function useAlberoCategorie(categorie: Categoria[] | null | undefined) {
  return useMemo(() => {
    const lista = categorie ?? [];
    const figli = new Map<string | null, Categoria[]>();
    for (const c of lista) {
      const k = c.padreId;
      figli.set(k, [...(figli.get(k) ?? []), c]);
    }
    const piatto: { categoria: Categoria; livello: number }[] = [];
    const scendi = (padre: string | null, livello: number) => {
      for (const c of (figli.get(padre) ?? []).sort((a, b) => a.ordine - b.ordine || a.nome.localeCompare(b.nome))) {
        piatto.push({ categoria: c, livello });
        scendi(c.id, livello + 1);
      }
    };
    scendi(null, 0);
    return { piatto, figli, perId: new Map(lista.map((c) => [c.id, c])) };
  }, [categorie]);
}

/**
 * Le linguette in cima a una pagina, nello stile delle `nav-tabs-custom` di
 * AdminLTE 2.
 *
 * Sono **pulsanti e non collegamenti**: cambiano cosa si guarda dentro la
 * stessa pagina, non dove si è. Chi naviga da tastiera le trova con Tab e le
 * attiva con Invio o Spazio senza che si debba scrivere niente.
 */
export function Linguette<T extends string>({
  voci,
  attiva,
  onSceglie,
}: {
  voci: { chiave: T; testo: string }[];
  attiva: T;
  onSceglie: (chiave: T) => void;
}) {
  return (
    <div className="linguette" role="tablist">
      {voci.map((v) => (
        <button
          key={v.chiave}
          role="tab"
          type="button"
          aria-selected={v.chiave === attiva}
          className="linguetta"
          onClick={() => onSceglie(v.chiave)}
        >
          {v.testo}
        </button>
      ))}
    </div>
  );
}
